import React, { FC, useEffect, useState } from 'react';
import { Printer } from '~reducers';
import { TroubleShootButtons, TroubleShootingTextWrapper } from '.';
import StepsComponent, { TroubleShootPageContent } from '../Components/StepsComponent';
import Heading from '~ComponentsImproved/Typography/Heading';
import Paragraph from '~ComponentsImproved/Typography/Paragraph';
import Button from '~ComponentsImproved/Button';
import ReceiptsNotPrinting from '../Components/ReceiptsNotPrinting';
import BongsNotPrinting from '../Components/BongsNotPrinting';
import ContactSupport from '../Components/ContactSupport';

// Images
import starLightPowerImage from '../../Images/star-power-light.png';
import epsonLightPowerImage from '../../Images/epson-power-light.png';
import phoneToPower from '../../Images/phone-to-power.png';
import starEthernetOff from '../../Images/star-ethernet-off.gif';
import starEthernetOn from '../../Images/star-ethernet-on.gif';
import starEthernetBlinking from '../../Images/star-ethernet-blinking.gif';
import epsonEthernetOff from '../../Images/epson-ethernet-off.png';
import epsonEthernetOn from '../../Images/epson-ethernet-on.png';
import epsonEthernetBlinking from '../../Images/epson-ethernet-blinking.gif';
import { useDeviceInfo, useI18n, useLogger, usePrinterApi, useSelector } from '~Hooks';
import { CircleSpinner } from 'react-spinners-kit';

interface Props {
  printer: Printer;
  closeGuide: () => void;
  testPrint: () => void;
  assignStation: () => void;
  sessionId: string;
}

type STEPS =
  | 'ISSUE_WITH_QUESTION'
  | 'LIGHTS_QUESTION'
  | 'CHECK_POWER_CABLE'
  | 'TEST_POWER'
  | 'NO_RECEIPT_PRINTING'
  | 'NO_BONGS_PRINTING'
  | 'CONTACT_SUPPORT'
  | 'LAN_LIGHTS_QUESTION'
  | 'ETHERNET_OFF'
  | 'TEST_OTHER_CABLE'
  | 'RESTART_ROUTER'
  | 'ETHERNET_ON'
  | 'ETHERNET_ON_NEXT'
  | 'ETHERNET_BLINKING'
  | 'ETHERNET_BLINKING_NEXT'
  | 'TRYING_RECONNECT'
  | 'CHECK_COVER';

const LANTroubleShooting: FC<Props> = ({
  printer,
  closeGuide,
  testPrint,
  assignStation,
  sessionId,
}) => {
  const logger = useLogger('printer-troubleshooting');
  const deviceInfo = useDeviceInfo();
  const { i18n } = useI18n();
  const printerApi = usePrinterApi();
  const [currentStep, setCurrentStep] = useState<STEPS>('ISSUE_WITH_QUESTION');
  const [noInternet, setNoInternet] = useState(false);
  const isScanning = useSelector((state) => state.POSPrinterScanActive);

  useEffect(() => {
    logger.info('Troubleshooting step: ' + currentStep, {
      troubleShooting: {
        printerId: printer.id,
        printerName: printerApi.getNameFromId(printer.id),
        model: printer.model,
        createdAt: printer.createdAt,
        lastPrinted: printer.lastPrinted,
        troubleShootingSessionId: sessionId,
      },
    });
  }, [currentStep]);

  const isStarPrinter = printer.model.toLowerCase().includes('mc_print');

  const checkInternet = async () => {
    const printerRequiresNetwork =
      !printer.id.toLowerCase().includes('bluetooth') &&
      !printer.id.toLowerCase().includes('usb') &&
      !printer.id.toLowerCase().includes('bt:');

    // Only check internet connection if the printer requires network (Lan and Cloud)
    if (printerRequiresNetwork) {
      try {
        const reponse = await fetch('https://www.google.com', { mode: 'no-cors' });

        if (!reponse) {
          setNoInternet(true);
          return; // We return and do not try to get the printer status if there is no internet
        }

        setNoInternet(false);
      } catch (error) {
        setNoInternet(true);
        return; // We return and do not try to get the printer status if there is no internet
      }
    }
  };

  useEffect(() => {
    if (currentStep === 'TRYING_RECONNECT' && isStarPrinter) {
      // If it is a star lan printer, we will try to reconnect it in case IP has changed
      logger.info('Starting printer scan from printer troubleshooting', {
        troubleShooting: {
          printer,
          troubleShootingSessionId: sessionId,
        },
      });
      window.postMessageReact('available-printers');
    }

    // TODO: Can we add a "reconnect" for epson printers that has changed IP?
  }, [currentStep, isStarPrinter]);

  useEffect(() => {
    checkInternet();
  }, []);

  const isIpad = deviceInfo?.model.includes('iPad');

  const steps: {
    [key in STEPS]: TroubleShootPageContent<STEPS>;
  } = {
    ISSUE_WITH_QUESTION: {
      title: i18n('Printer.TS.IssueWith.Question'),
      buttons: [
        {
          text: i18n('Printer.TS.IssueWith.Receipts'),
          next: 'NO_RECEIPT_PRINTING',
        },
        {
          text: i18n('Printer.TS.IssueWith.KitchenTickets'),
          next: 'NO_BONGS_PRINTING',
        },
        {
          text: i18n('Printer.TS.IssueWith.Everything'),
          next: 'LIGHTS_QUESTION',
        },
      ],
    },
    LIGHTS_QUESTION: {
      title: i18n('Printer.TS.LightsQuestion'),
      images: [
        {
          src: isStarPrinter ? starLightPowerImage : epsonLightPowerImage,
        },
      ],
      buttons: [
        {
          text: i18n('Printer.Troubleshooting.No'),
          next: 'CHECK_POWER_CABLE',
        },
        {
          text: i18n('Printer.Troubleshooting.Yes'),
          next: isStarPrinter ? 'TRYING_RECONNECT' : 'CHECK_COVER',
        },
      ],
    },
    CHECK_COVER: {
      // ONLY FOR EPSON PRINTERS, since the status is not working properly for epson printers
      title: i18n('Printer.TS.CheckCover'),
      buttons: [
        {
          text: i18n('Printer.Troubleshooting.Done'),
          next: 'LAN_LIGHTS_QUESTION',
        },
      ],
    },
    LAN_LIGHTS_QUESTION: {
      title: i18n('Printer.TS.LAN.LightStatus.Question'),
      images: [
        {
          src: isStarPrinter ? starEthernetOff : epsonEthernetOff,
          height: isStarPrinter ? 100 : 70,
        },
        {
          src: isStarPrinter ? starEthernetOn : epsonEthernetOn,
          height: isStarPrinter ? 100 : 70,
        },
        {
          src: isStarPrinter ? starEthernetBlinking : epsonEthernetBlinking,
          height: isStarPrinter ? 100 : 91,
        },
      ],
      buttons: [
        {
          text: i18n('Printer.TS.LAN.LightStatus.Off'),
          next: 'ETHERNET_OFF',
        },
        {
          text: i18n('Printer.TS.LAN.LightStatus.On'),
          next: 'ETHERNET_ON',
        },
        {
          text: i18n('Printer.TS.LAN.LightStatus.Blinking'),
          next: 'ETHERNET_BLINKING',
        },
      ],
    },
    ETHERNET_OFF: {
      title: i18n('Printer.TS.LAN.CableConnnected.Question'),
      buttons: [
        {
          text: i18n('Printer.Troubleshooting.Yes'),
          next: 'TEST_OTHER_CABLE',
        },
      ],
    },
    ETHERNET_ON: {
      title: i18n('Printer.TS.LAN.SameNetwork.Question', isIpad),
      text: i18n('Printer.TS.LAN.SameNetwork.Desc', isIpad),
      buttons: [
        {
          text: i18n('Printer.Troubleshooting.Yes'),
          next: 'ETHERNET_ON_NEXT',
        },
      ],
    },
    ETHERNET_ON_NEXT: {
      title: i18n('Printer.TS.LAN.TryTestPrint'),
      buttons: [
        {
          text: i18n('Printer.Troubleshooting.TestPrint'),
          action: 'TEST_PRINT',
        },
        {
          text: i18n('Printer.Troubleshooting.NotWorking'),
          next: 'CONTACT_SUPPORT',
        },
      ],
    },
    ETHERNET_BLINKING: {
      title: i18n('Printer.TS.LAN.CheckCables'),
      text: i18n('Printer.TS.LAN.CheckCables.Desc'),
      buttons: [
        {
          text: i18n('Printer.Troubleshooting.Done'),
          next: 'ETHERNET_BLINKING_NEXT',
        },
      ],
    },
    ETHERNET_BLINKING_NEXT: {
      title: i18n('Printer.TS.LAN.TryTestPrint'),
      buttons: [
        {
          text: i18n('Printer.Troubleshooting.TestPrint'),
          action: 'TEST_PRINT',
        },
        {
          text: i18n('Printer.Troubleshooting.NotWorking'),
          next: 'RESTART_ROUTER',
        },
      ],
    },
    TEST_OTHER_CABLE: {
      title: i18n('Printer.TS.LAN.TryOtherCable'),
      text: i18n('Printer.TS.LAN.TryOtherCable.Desc'),
      buttons: [
        {
          text: i18n('Printer.Troubleshooting.TestPrint'),
          action: 'TEST_PRINT',
        },
        {
          text: i18n('Printer.Troubleshooting.NotWorking'),
          next: 'RESTART_ROUTER',
        },
      ],
    },
    RESTART_ROUTER: {
      title: i18n('Printer.TS.LAN.RestartRouter'),
      text: i18n('Printer.TS.LAN.RestartRouter.Desc'),
      buttons: [
        {
          text: i18n('Printer.Troubleshooting.TestPrint'),
          action: 'TEST_PRINT',
        },
        {
          text: i18n('Printer.Troubleshooting.NotWorking'),
          next: 'CONTACT_SUPPORT',
        },
      ],
    },
    CHECK_POWER_CABLE: {
      title: i18n('Printer.TS.CablePluggedIn'),
      buttons: [
        {
          text: i18n('Printer.Troubleshooting.Yes'),
          next: 'TEST_POWER',
        },
      ],
    },
    TEST_POWER: {
      title: i18n('Printer.TS.PowerWorking'),
      text: i18n('Printer.TS.PowerWorking.Desc'),
      images: [
        {
          src: phoneToPower,
          height: 250,
        },
      ],
      buttons: [
        {
          text: i18n('Printer.Troubleshooting.Yes'),
          next: 'CONTACT_SUPPORT',
        },
      ],
    },
    NO_RECEIPT_PRINTING: {
      custom: (
        <ReceiptsNotPrinting
          printer={printer}
          closeGuide={closeGuide}
          testPrint={testPrint}
          printingWorks={(works) => setCurrentStep(works ? 'CONTACT_SUPPORT' : 'LIGHTS_QUESTION')}
          sessionId={sessionId}
        />
      ),
    },
    NO_BONGS_PRINTING: {
      custom: (
        <BongsNotPrinting
          printer={printer}
          closeGuide={closeGuide}
          goToContactSupport={() => setCurrentStep('CONTACT_SUPPORT')}
          assignStation={() => {
            closeGuide();
            assignStation();
          }}
          sessionId={sessionId}
        />
      ),
    },
    CONTACT_SUPPORT: {
      custom: <ContactSupport />,
    },
    TRYING_RECONNECT: {
      custom: (
        <>
          <TroubleShootingTextWrapper>
            <Heading level={1} color="#fff" bottom={16}>
              {i18n('Printer.TS.LAN.Reconnecting')}
            </Heading>
            <Paragraph level={2} bottom={24} color="var(--colorBlueGray200)">
              {i18n('Printer.TS.LAN.Reconnecting.Desc')}
            </Paragraph>
          </TroubleShootingTextWrapper>

          {isScanning ? (
            <CircleSpinner />
          ) : (
            <TroubleShootButtons>
              <Button
                text={i18n('Printer.Troubleshooting.TestPrint')}
                styleType="OK"
                onTouchStart={() => {
                  testPrint();
                }}
              />
              <Button
                text={i18n('Printer.Troubleshooting.NotWorking')}
                styleType="OK"
                onTouchStart={() => {
                  setCurrentStep('LAN_LIGHTS_QUESTION');
                }}
              />
            </TroubleShootButtons>
          )}
        </>
      ),
    },
  };

  return (
    <>
      {noInternet ? (
        <>
          <TroubleShootingTextWrapper>
            <Heading level={1} color="#fff" bottom={16}>
              {i18n('Printer.TS.LAN.NoInternet')}
            </Heading>
            <Paragraph level={2} color="var(--colorBlueGray200)">
              {i18n('Printer.TS.LAN.NoInternet.Desc')}
            </Paragraph>
          </TroubleShootingTextWrapper>

          <TroubleShootButtons>
            <Button text="Test print" styleType="OK" onTouchStart={testPrint} />
            <Button text="OK" styleType="OK" onTouchStart={closeGuide} />
          </TroubleShootButtons>
        </>
      ) : (
        <StepsComponent<STEPS>
          steps={steps}
          testPrint={testPrint}
          currentStep={currentStep}
          setCurrentStep={setCurrentStep}
        />
      )}
    </>
  );
};

export default LANTroubleShooting;
